import { useEffect, useState } from "react";
import api, { formatApiError, TOKEN_KEY, SA_TOKEN_KEY } from "../../lib/api";
import { formatDate, formatEuro } from "../../lib/format";
import PageHeader from "../../components/PageHeader";
import { Building2, Plus, Loader2, Power, Pencil, Trash2, AlertTriangle, FileJson, Download, Upload, FileSpreadsheet, KeyRound, LogIn, Blocks } from "lucide-react";
import { ModuliStudioDialog, ModuliPianiDialog } from "../../components/ModuliStudioDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { downloadCompagnieTemplate, downloadCompagnieExcelTemplate, parseCompagnieFile, companyName } from "../../lib/compagnieImport";
import CompagniePreview from "../../components/CompagniePreview";
import { DemoSection } from "../../components/DemoSection";
import { toast } from "sonner";
import OwnerDatiFields from "./OwnerDatiFields";
import { PianoSelect } from "../../components/PianoSelect";
import { PianoChangeConfirm } from "../../components/PianoChangeConfirm";
import { validatePIVA } from "../../lib/piva";

const OWNER_FIELDS = ["owner_nome", "owner_cognome", "owner_data_nascita", "owner_sesso", "owner_luogo_nascita", "owner_luogo_nascita_prov", "numero_civico", "cap", "provincia"];
const splitName = (n) => { const p = (n || "").trim().split(/\s+/); return { nome: p[0] || "", cognome: p.slice(1).join(" ") }; };

export default function Studi() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ studio_name: "", owner_name: "", owner_email: "", owner_password: "", piano: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tenants");
      setTenants(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/tenants", form);
      toast.success("Studio creato con successo");
      setOpen(false);
      setForm({ studio_name: "", owner_name: "", owner_email: "", owner_password: "", piano: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (t) => {
    const next = t.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/admin/tenants/${t.id}/status`, { status: next });
      toast.success(next === "active" ? "Studio riattivato" : "Studio sospeso");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [pianoPending, setPianoPending] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [credModal, setCredModal] = useState(null); // { username, password }
  const [moduliTenant, setModuliTenant] = useState(null);
  const [pianiOpen, setPianiOpen] = useState(false);
  const [credSending, setCredSending] = useState(false);
  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#%*-_";
    const a = new Uint32Array(14);
    (window.crypto || window.msCrypto).getRandomValues(a);
    let p = "";
    for (let i = 0; i < 14; i++) p += chars[a[i] % chars.length];
    return p;
  };
  const openCredModal = () => {
    const email = (editForm.owner_email || "").trim();
    if (!email) { toast.error("Inserisci prima l'email titolare"); return; }
    setCredModal({ username: email, password: genPassword() });
  };
  const sendCredentials = async () => {
    setCredSending(true);
    try {
      const { data } = await api.post(`/admin/tenants/${editForm.id}/send-credentials`, { password: credModal.password, app_url: window.location.origin });
      toast.success(`Credenziali inviate a ${credModal.username}`);
      setCredModal(null);
      if (data?.log) setCredLog((l) => [data.log, ...l.filter((x) => x.id !== data.log.id)]);
      loadCredLog(editForm.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setCredSending(false); }
  };
  const [credLog, setCredLog] = useState([]);
  const loadCredLog = async (tid) => {
    try {
      const { data } = await api.get(`/admin/tenants/${tid}/credenziali-log`);
      setCredLog(data);
    } catch (e) { setCredLog([]); }
  };
  const [editForm, setEditForm] = useState({ id: "", studio_name: "", owner_name: "", owner_email: "", piano: "", data_attivazione: "", data_scadenza: "", partita_iva: "", codice_fiscale: "", indirizzo: "", citta: "", telefono: "", cellulare: "", fax: "", email_contatto: "", pec: "", owner_nome: "", owner_cognome: "", owner_data_nascita: "", owner_sesso: "", owner_luogo_nascita: "", owner_luogo_nascita_prov: "", numero_civico: "", cap: "", provincia: "", pdf_footer: true, impexp_enabled: false, ai_lettere_enabled: false, foto_ai_targa_enabled: false, wa_signature_enabled: false, wa_signature_text: "", demo_enabled: false, demo_retention_days: "", demo_scadenza_tipo: "data", demo_scadenza_data: "", demo_scadenza_giorni: "", demo_attivato_at: "" });

  const openEdit = (t) => {
    setEditForm({
      id: t.id, studio_name: t.studio_name || "", owner_name: t.owner_name || "", owner_email: t.owner_email || "", piano: t.piano || "",
      data_attivazione: t.data_attivazione || "", data_scadenza: t.data_scadenza || "",
      partita_iva: t.partita_iva || "", codice_fiscale: t.codice_fiscale || "", indirizzo: t.indirizzo || "", citta: t.citta || "",
      telefono: t.telefono || "", cellulare: t.cellulare || "", fax: t.fax || "", email_contatto: t.email_contatto || "", pec: t.pec || "",
      ...Object.fromEntries(OWNER_FIELDS.map((f) => [f, t[f] || ""])),
      ...(!t.owner_nome && !t.owner_cognome ? { owner_nome: splitName(t.owner_name).nome, owner_cognome: splitName(t.owner_name).cognome } : {}),
      pdf_footer: t.pdf_footer !== false,
      impexp_enabled: !!t.impexp_enabled,
      ai_lettere_enabled: !!t.ai_lettere_enabled,
      foto_ai_targa_enabled: !!t.foto_ai_targa_enabled,
      limite_utenti: t.limite_utenti ?? 2,
      limite_utenti_log: t.limite_utenti_log || [],
      wa_signature_enabled: !!t.wa_signature_enabled,
      wa_signature_text: t.wa_signature_text || "",
      demo_enabled: !!t.demo?.enabled, demo_retention_days: t.demo?.retention_days ?? "",
      demo_scadenza_tipo: t.demo?.scadenza_tipo || "data", demo_scadenza_data: t.demo?.scadenza_data || "",
      demo_scadenza_giorni: t.demo?.scadenza_giorni || "", demo_attivato_at: t.demo?.enabled ? (t.demo?.attivato_at || "") : "", demo_codice: t.demo?.enabled ? (t.demo?.codice || "") : "", demo_password: t.demo?.password || "",
    });
    setEditOpen(true);
    loadPayments(t.id);
    loadCredLog(t.id);
  };

  const [payments, setPayments] = useState([]);
  const [newPay, setNewPay] = useState({ importo: "", data: "", descrizione: "" });
  const [payBusy, setPayBusy] = useState(false);

  const loadPayments = async (tid) => {
    setPayments([]);
    try {
      const { data } = await api.get(`/admin/tenants/${tid}/pagamenti`);
      setPayments(data);
    } catch (e) { /* ignore */ }
  };

  const addPayment = async () => {
    if (!newPay.importo || !newPay.data) { toast.error("Inserisci importo e data"); return; }
    setPayBusy(true);
    try {
      await api.post(`/admin/tenants/${editForm.id}/pagamenti`, {
        importo: parseFloat(newPay.importo), data: newPay.data, descrizione: newPay.descrizione,
      });
      setNewPay({ importo: "", data: "", descrizione: "" });
      loadPayments(editForm.id);
      toast.success("Pagamento aggiunto");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPayBusy(false); }
  };

  const deletePayment = async (pid) => {
    try {
      await api.delete(`/admin/tenants/${editForm.id}/pagamenti/${pid}`);
      loadPayments(editForm.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // --- Import compagnie JSON (cross-tenant) ---
  const [impOpen, setImpOpen] = useState(false);
  const [impCompanies, setImpCompanies] = useState(null);
  const [impFileName, setImpFileName] = useState("");
  const [impStrategy, setImpStrategy] = useState("skip");
  const [impAll, setImpAll] = useState(true);
  const [impSelected, setImpSelected] = useState([]);
  const [impBusy, setImpBusy] = useState(false);
  const [impReport, setImpReport] = useState(null);

  const resetImp = () => { setImpCompanies(null); setImpFileName(""); setImpStrategy("skip"); setImpAll(true); setImpSelected([]); setImpReport(null); };

  const onImpFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImpReport(null);
    try {
      const arr = (await parseCompagnieFile(file)).filter((c) => companyName(c));
      if (arr.length === 0) { toast.error("Il file non contiene compagnie valide (manca il nome)."); return; }
      setImpFileName(file.name);
      setImpCompanies(arr);
    } catch (err) { toast.error(err.message || "File non valido"); }
  };

  const toggleImpStudio = (id) => setImpSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const doAdminImport = async () => {
    if (!impCompanies) return;
    if (!impAll && impSelected.length === 0) { toast.error("Seleziona almeno uno studio"); return; }
    setImpBusy(true);
    try {
      const { data } = await api.post("/admin/compagnie/import-json", {
        compagnie: impCompanies, strategy: impStrategy,
        tenant_ids: impAll ? "all" : impSelected,
      });
      setImpReport(data);
      toast.success("Importazione completata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImpBusy(false); }
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d - today) / 86400000);
  };

  const inScadenza = tenants
    .filter((t) => {
      const g = daysUntil(t.data_scadenza);
      return g !== null && g <= 30;
    })
    .sort((a, b) => daysUntil(a.data_scadenza) - daysUntil(b.data_scadenza));

  const saveEdit = async (e) => {
    e.preventDefault();
    const pv = validatePIVA(editForm.partita_iva);
    if (!pv.ok) { toast.error(pv.error); return; }
    setEditSaving(true);
    try {
      const fullName = `${editForm.owner_nome || ""} ${editForm.owner_cognome || ""}`.trim();
      await api.patch(`/admin/tenants/${editForm.id}`, {
        studio_name: editForm.studio_name, owner_name: fullName || editForm.owner_name,
        ...Object.fromEntries(OWNER_FIELDS.map((f) => [f, editForm[f] || ""])),
        owner_email: editForm.owner_email, piano: editForm.piano,
        data_attivazione: editForm.data_attivazione, data_scadenza: editForm.data_scadenza,
        partita_iva: editForm.partita_iva, codice_fiscale: editForm.codice_fiscale,
        indirizzo: editForm.indirizzo, citta: editForm.citta, telefono: editForm.telefono,
        cellulare: editForm.cellulare, fax: editForm.fax, email_contatto: editForm.email_contatto, pec: editForm.pec,
        pdf_footer: editForm.pdf_footer,
        impexp_enabled: editForm.impexp_enabled,
        ai_lettere_enabled: editForm.ai_lettere_enabled,
        foto_ai_targa_enabled: editForm.foto_ai_targa_enabled,
        limite_utenti: Math.max(1, parseInt(editForm.limite_utenti, 10) || 1),
        wa_signature_enabled: editForm.wa_signature_enabled,
        wa_signature_text: editForm.wa_signature_text,
        demo_enabled: editForm.demo_enabled,
        ...(editForm.demo_enabled ? {
          demo_retention_days: parseInt(editForm.demo_retention_days, 10) || 0,
          demo_scadenza_tipo: editForm.demo_scadenza_tipo,
          demo_scadenza_data: editForm.demo_scadenza_data,
          demo_scadenza_giorni: parseInt(editForm.demo_scadenza_giorni, 10) || 0,
        } : {}),
      });
      toast.success("Studio aggiornato");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEditSaving(false); }
  };

  const accediComeStudio = async (t) => {
    try {
      const { data } = await api.post(`/admin/tenants/${t.id}/impersona`);
      localStorage.setItem(SA_TOKEN_KEY, localStorage.getItem(TOKEN_KEY) || "");
      localStorage.setItem(TOKEN_KEY, data.token);
      window.location.href = "/";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };


  const removeStudio = async (t) => {
    if (!window.confirm(`Sei sicuro di voler eliminare lo studio ${t.studio_name}? Questa azione è irreversibile.`)) return;
    try {
      await api.delete(`/admin/tenants/${t.id}`);
      toast.success("Studio eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-studi">
      <PageHeader
        title="Gestione Studi"
        subtitle="Crea, sospendi e monitora gli studi (tenant) presenti sulla piattaforma."
        icon={Building2}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setPianiOpen(true)} data-testid="moduli-piani-button" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <Blocks className="h-4 w-4 mr-1" /> Moduli per piano
            </Button>
            <Button variant="outline" onClick={() => { resetImp(); setImpOpen(true); }} data-testid="admin-import-compagnie-button"
              className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <FileJson className="h-4 w-4 mr-1" /> Importa Compagnie JSON
            </Button>          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-studio-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Nuovo Studio
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121824] border-slate-700 text-slate-100">
              <DialogHeader>
                <DialogTitle className="font-head">Crea nuovo studio</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="new-studio-form">
                <div>
                  <Label className="text-slate-300">Nome studio</Label>
                  <Input required value={form.studio_name} onChange={(e) => setForm({ ...form, studio_name: e.target.value })}
                    data-testid="studio-name-input" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Nome titolare</Label>
                  <Input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                    data-testid="owner-name-input" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Email titolare</Label>
                  <Input required type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                    data-testid="owner-email-input" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Password titolare</Label>
                  <Input required type="password" minLength={6} value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                    data-testid="owner-password-input" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Piano <span className="text-slate-500 font-normal">(facoltativo: applica i moduli del piano)</span></Label>
                  <PianoSelect value={form.piano} onChange={(v) => setForm({ ...form, piano: v })} testId="studio-piano-input" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="submit-studio-button"
                    className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea studio"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {inScadenza.length > 0 && (
        <div className="mb-5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 pgp-fade-up" data-testid="scadenze-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-semibold text-amber-300">Abbonamenti in scadenza (prossimi 30 giorni)</span>
          </div>
          <ul className="space-y-1">
            {inScadenza.map((t) => {
              const g = daysUntil(t.data_scadenza);
              const scaduto = g < 0;
              return (
                <li key={t.id} className="flex items-center justify-between text-sm" data-testid={`scadenza-item-${t.id}`}>
                  <button onClick={() => openEdit(t)} className="text-slate-200 hover:text-amber-300 transition-colors text-left">
                    {t.studio_name}
                  </button>
                  <span className={scaduto ? "text-red-400 font-semibold font-mono text-xs" : "text-amber-300 font-mono text-xs"}>
                    {scaduto ? `Scaduto il ${formatDate(t.data_scadenza)}` : g === 0 ? `Scade oggi (${formatDate(t.data_scadenza)})` : `Scade tra ${g} gg (${formatDate(t.data_scadenza)})`}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="pgp-card overflow-hidden pgp-fade-up">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : tenants.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">Nessuno studio registrato.</div>
        ) : (
          <table className="w-full text-sm" data-testid="studi-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                <th className="px-5 py-3">Studio</th>
                <th className="px-5 py-3">Titolare</th>
                <th className="px-5 py-3">Operatori</th>
                <th className="px-5 py-3">Clienti</th>
                <th className="px-5 py-3">Creato</th>
                <th className="px-5 py-3">Scadenza</th>
                <th className="px-5 py-3">Stato</th>
                <th className="px-5 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {tenants.map((t) => (
                <tr key={t.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`studio-row-${t.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-100">
                    {t.studio_name}
                    {t.demo?.enabled && (
                      <Badge className="ml-2 bg-amber-500/15 text-amber-400 border border-amber-500/40 hover:bg-amber-500/15" data-testid={`studio-demo-badge-${t.id}`}>
                        Demo · scade il {t.demo_scadenza ? formatDate(t.demo_scadenza) : "—"}
                      </Badge>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-[11px] font-normal">
                      {t.piano
                        ? <span className="rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-px text-sky-300" data-testid={`studio-piano-${t.id}`}>{t.piano}</span>
                        : <span className="text-slate-500" data-testid={`studio-piano-${t.id}`}>Nessun piano</span>}
                      <span className={t.moduli_off ? "text-red-300" : "text-emerald-400"} data-testid={`studio-moduli-off-${t.id}`}>
                        {t.moduli_off ? `${t.moduli_off} ${t.moduli_off === 1 ? "modulo spento" : "moduli spenti"}` : "Tutti i moduli attivi"}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-300">
                    {t.owner_name}
                    <div className="text-[11px] text-slate-500 font-mono">{t.owner_email}</div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-300">{t.operatori}</td>
                  <td className="px-5 py-3.5 text-slate-300">{t.clienti}</td>
                  <td className="px-5 py-3.5 text-slate-400 font-mono text-xs">{formatDate(t.created_at)}</td>
                  <td className="px-5 py-3.5 font-mono text-xs" data-testid={`studio-scadenza-${t.id}`}>
                    {t.data_scadenza ? (
                      <span className={isExpired(t.data_scadenza) ? "text-red-400 font-semibold" : "text-slate-400"}>
                        {formatDate(t.data_scadenza)}
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {t.status === "active" ? (
                      <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15">Attivo</Badge>
                    ) : (
                      <Badge className="bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/15">Sospeso</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => toggleStatus(t)} data-testid={`toggle-status-${t.id}`}
                        title={t.status === "active" ? "Sospendi" : "Riattiva"}
                        className={t.status === "active" ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 w-8 p-0" : "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-8 w-8 p-0"}>
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => accediComeStudio(t)} data-testid={`impersona-studio-${t.id}`} title="Accedi come studio"
                        className="text-slate-300 hover:text-amber-300 hover:bg-amber-500/10 h-8 px-2 gap-1 text-xs">
                        <LogIn className="h-4 w-4" /> Accedi come studio
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setModuliTenant(t)} data-testid={`moduli-studio-${t.id}`} title="Gestione Moduli"
                        className="text-slate-300 hover:text-amber-300 hover:bg-amber-500/10 h-8 px-2 gap-1 text-xs">
                        <Blocks className="h-4 w-4" /> Moduli
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-studio-${t.id}`} title="Modifica studio"
                        className="text-slate-300 hover:text-cyan-300 hover:bg-cyan-500/10 h-8 w-8 p-0">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeStudio(t)} data-testid={`delete-studio-${t.id}`} title="Elimina studio"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto" data-testid="edit-studio-dialog">
          <DialogHeader><DialogTitle className="font-head">Modifica studio</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4" data-testid="edit-studio-form">
            <div>
              <Label className="text-slate-300">Nome studio</Label>
              <Input required value={editForm.studio_name} onChange={(e) => setEditForm({ ...editForm, studio_name: e.target.value })}
                data-testid="edit-studio-name" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Nome titolare</Label>
                <Input value={editForm.owner_nome} onChange={(e) => setEditForm({ ...editForm, owner_nome: e.target.value })}
                  data-testid="edit-owner-name" className="mt-1.5 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-slate-300">Cognome titolare</Label>
                <Input value={editForm.owner_cognome} onChange={(e) => setEditForm({ ...editForm, owner_cognome: e.target.value })}
                  data-testid="edit-owner-cognome" className="mt-1.5 bg-slate-900/60 border-slate-700" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Email titolare</Label>
              <Input type="email" value={editForm.owner_email} onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value })}
                data-testid="edit-owner-email" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300">Piano</Label>
              <PianoSelect value={editForm.piano} onChange={(v) => { if (v !== editForm.piano) setPianoPending(v); }} testId="edit-piano" />
              {pianoPending !== null && (
                <PianoChangeConfirm tenantId={editForm.id} from={editForm.piano} to={pianoPending}
                  onConfirm={() => { setEditForm((f) => ({ ...f, piano: pianoPending })); setPianoPending(null); }}
                  onCancel={() => setPianoPending(null)} />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Data Attivazione</Label>
                <Input type="date" value={editForm.data_attivazione} onChange={(e) => setEditForm({ ...editForm, data_attivazione: e.target.value })}
                  data-testid="edit-data-attivazione" className="mt-1.5 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-slate-300">Data Scadenza Abbonamento</Label>
                <Input type="date" value={editForm.data_scadenza} onChange={(e) => setEditForm({ ...editForm, data_scadenza: e.target.value })}
                  data-testid="edit-data-scadenza" className={`mt-1.5 bg-slate-900/60 border-slate-700 ${isExpired(editForm.data_scadenza) ? "text-red-400 border-red-500/50" : ""}`} />
                {isExpired(editForm.data_scadenza) && (
                  <p className="text-[11px] text-red-400 mt-1" data-testid="edit-scadenza-warning">Abbonamento scaduto</p>
                )}
              </div>
            </div>

            <div className="pt-2 border-t border-slate-800">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 font-mono mb-3">Dati studio / titolare</p>
              <OwnerDatiFields f={editForm} set={(patch) => setEditForm((x) => ({ ...x, ...patch }))} />
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-300">Telefono</Label>
                  <Input value={editForm.telefono} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })}
                    data-testid="edit-telefono" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Cellulare</Label>
                  <Input value={editForm.cellulare} onChange={(e) => setEditForm({ ...editForm, cellulare: e.target.value })}
                    data-testid="edit-cellulare" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Fax</Label>
                  <Input value={editForm.fax} onChange={(e) => setEditForm({ ...editForm, fax: e.target.value })}
                    data-testid="edit-fax" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Email studio</Label>
                  <Input value={editForm.email_contatto} onChange={(e) => setEditForm({ ...editForm, email_contatto: e.target.value })}
                    data-testid="edit-email-contatto" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">PEC</Label>
                  <Input value={editForm.pec} onChange={(e) => setEditForm({ ...editForm, pec: e.target.value })}
                    data-testid="edit-pec" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-800" data-testid="storico-pagamenti">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 font-mono mb-3">Storico pagamenti / rinnovi</p>
              {payments.length === 0 ? (
                <p className="text-sm text-slate-500 mb-3">Nessun pagamento registrato.</p>
              ) : (
                <ul className="space-y-1.5 mb-3">
                  {payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between rounded-md bg-slate-900/60 border border-slate-700 px-3 py-2" data-testid={`pagamento-${p.id}`}>
                      <div className="min-w-0">
                        <span className="text-slate-100 font-medium font-mono">{formatEuro(p.importo)}</span>
                        <span className="text-slate-400 text-xs font-mono ml-3">{formatDate(p.data)}</span>
                        {p.descrizione && <div className="text-[11px] text-slate-500 truncate">{p.descrizione}</div>}
                      </div>
                      <Button type="button" size="sm" variant="ghost" onClick={() => deletePayment(p.id)} data-testid={`delete-pagamento-${p.id}`}
                        title="Elimina pagamento" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0 shrink-0">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <Label className="text-slate-400 text-xs">Importo €</Label>
                  <Input type="number" step="0.01" min="0" value={newPay.importo} onChange={(e) => setNewPay({ ...newPay, importo: e.target.value })}
                    data-testid="pagamento-importo" placeholder="0,00" className="mt-1 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Data</Label>
                  <Input type="date" value={newPay.data} onChange={(e) => setNewPay({ ...newPay, data: e.target.value })}
                    data-testid="pagamento-data" className="mt-1 bg-slate-900/60 border-slate-700" />
                </div>
                <Button type="button" onClick={addPayment} disabled={payBusy} data-testid="add-pagamento-button"
                  className="bg-slate-700 hover:bg-slate-600 text-slate-100">
                  {payBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Aggiungi</>}
                </Button>
              </div>
              <Input value={newPay.descrizione} onChange={(e) => setNewPay({ ...newPay, descrizione: e.target.value })}
                data-testid="pagamento-descrizione" placeholder="Descrizione (opzionale, es. Rinnovo annuale)" className="mt-2 bg-slate-900/60 border-slate-700" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3" data-testid="pdf-footer-setting">
              <div>
                <div className="text-sm text-slate-200">Footer PDF lettere</div>
                <div className="text-xs text-slate-500">Aggiunge "Elaborato con PatroGestPro" e il numero di pagina in fondo alle lettere PDF.</div>
              </div>
              <Switch checked={editForm.pdf_footer} onCheckedChange={(v) => setEditForm({ ...editForm, pdf_footer: v })} data-testid="edit-pdf-footer" className="data-[state=checked]:bg-amber-500" />
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 space-y-2" data-testid="limite-utenti-setting">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-slate-200">Limite utenti</div>
                  <div className="text-xs text-slate-500">Numero massimo di utenti dello studio (titolare incluso; esclusi i clienti dell'Area Cliente).</div>
                </div>
                <Input type="number" min={1} value={editForm.limite_utenti ?? 2} onChange={(e) => setEditForm({ ...editForm, limite_utenti: e.target.value })} data-testid="edit-limite-utenti" className="w-24 bg-slate-900/60 border-slate-700 text-right" />
              </div>
              <div data-testid="limite-utenti-log">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Cronologia modifiche</div>
                {(editForm.limite_utenti_log || []).length ? (
                  <ul className="max-h-32 overflow-y-auto space-y-1">
                    {editForm.limite_utenti_log.map((l, i) => (
                      <li key={i} className="flex items-center justify-between text-xs text-slate-300" data-testid={`limite-utenti-log-${i}`}>
                        <span className="font-mono text-slate-400">{new Date(l.at).toLocaleDateString("it-IT")} {new Date(l.at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                        <span><span className="text-slate-500">{l.old}</span> → <span className="font-semibold text-amber-300">{l.new}</span></span>
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-xs text-slate-500" data-testid="limite-utenti-log-empty">Nessuna modifica registrata.</div>}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3" data-testid="impexp-setting">
              <div>
                <div className="text-sm text-slate-200">Sezione Import/Export</div>
                <div className="text-xs text-slate-500">Abilita allo studio importazione, esportazione e backup dei dati. Disattivata per impostazione predefinita.</div>
              </div>
              <Switch checked={!!editForm.impexp_enabled} onCheckedChange={(v) => setEditForm({ ...editForm, impexp_enabled: v })} data-testid="edit-impexp-enabled" className="data-[state=checked]:bg-amber-500" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3" data-testid="ai-lettere-setting">
              <div>
                <div className="text-sm text-slate-200">Scrivi con AI (Gestione Lettere)</div>
                <div className="text-xs text-slate-500">Abilita allo studio la generazione automatica delle lettere con l'AI. Disattivata per impostazione predefinita.</div>
              </div>
              <Switch checked={!!editForm.ai_lettere_enabled} onCheckedChange={(v) => setEditForm({ ...editForm, ai_lettere_enabled: v })} data-testid="edit-ai-lettere-enabled" className="data-[state=checked]:bg-amber-500" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3" data-testid="foto-ai-targa-setting">
              <div>
                <div className="text-sm text-slate-200">Cerca targa con AI (pagina Foto)</div>
                <div className="text-xs text-slate-500">Mostra nella pagina /foto il pulsante di lettura targa con AI (consuma crediti). Disattivata per impostazione predefinita.</div>
              </div>
              <Switch checked={!!editForm.foto_ai_targa_enabled} onCheckedChange={(v) => setEditForm({ ...editForm, foto_ai_targa_enabled: v })} data-testid="edit-foto-ai-targa-enabled" className="data-[state=checked]:bg-amber-500" />
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 space-y-2" data-testid="wa-signature-setting">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-slate-200">Firma pubblicitaria WhatsApp</div>
                  <div className="text-xs text-slate-500">Aggiunta in fondo ai messaggi WhatsApp preimpostati dello studio. Disattivata per impostazione predefinita.</div>
                </div>
                <Switch checked={!!editForm.wa_signature_enabled} onCheckedChange={(v) => setEditForm({ ...editForm, wa_signature_enabled: v })} data-testid="edit-wa-signature-enabled" className="data-[state=checked]:bg-amber-500" />
              </div>
              <textarea value={editForm.wa_signature_text || ""} rows={2} onChange={(e) => setEditForm({ ...editForm, wa_signature_text: e.target.value })} data-testid="edit-wa-signature-text"
                className="w-full resize-y rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500/60" />
            </div>
            <DemoSection form={editForm} setForm={setEditForm} />
            <DialogFooter>
              <Button type="button" onClick={openCredModal} disabled={!editForm.owner_email || !editForm.owner_email.trim()} data-testid="gen-send-credentials-button" className="mr-auto bg-teal-600 hover:bg-teal-700 text-white font-semibold disabled:opacity-40">
                <KeyRound className="h-4 w-4 mr-1" /> Genera e Invia Credenziali
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
              <Button type="submit" disabled={editSaving} data-testid="save-studio-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modifiche"}
              </Button>
            </DialogFooter>
            <div className="pt-3 border-t border-slate-800" data-testid="storico-credenziali">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 font-mono mb-3">Cronologia invii credenziali</p>
              {credLog.length === 0 ? (
                <p className="text-sm text-slate-500 mb-1" data-testid="cred-log-empty">Nessun invio registrato.</p>
              ) : (
                <ul className="space-y-1.5 mb-1 max-h-56 overflow-y-auto">
                  {credLog.map((l) => {
                    const d = new Date(l.sent_at);
                    return (
                      <li key={l.id} className="grid grid-cols-[90px_60px_1fr_auto] items-center gap-3 rounded-md bg-slate-900/60 border border-slate-700 px-3 py-2 text-xs" data-testid={`cred-log-${l.id}`}>
                        <span className="font-mono text-slate-100" data-testid={`cred-log-date-${l.id}`}>{d.toLocaleDateString("it-IT")}</span>
                        <span className="font-mono text-slate-300" data-testid={`cred-log-time-${l.id}`}>{d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                        <span className="text-slate-200 truncate" title={l.to} data-testid={`cred-log-to-${l.id}`}>{l.to}</span>
                        <KeyRound className="h-3.5 w-3.5 text-teal-400 shrink-0" />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {moduliTenant && <ModuliStudioDialog tenant={moduliTenant} onClose={() => { setModuliTenant(null); load(); }} />}
      {pianiOpen && <ModuliPianiDialog onClose={() => { setPianiOpen(false); load(); }} />}
      <Dialog open={!!credModal} onOpenChange={(o) => { if (!o) setCredModal(null); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="cred-modal">
          <DialogHeader><DialogTitle className="font-head">Riepilogo credenziali</DialogTitle></DialogHeader>
          {credModal && (
            <div className="space-y-3">
              <p className="text-sm text-slate-400">Queste credenziali verranno inviate via email al titolare. Annotale ora se necessario.</p>
              <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 space-y-2 font-mono text-sm">
                <div><span className="text-slate-500">Username:</span> <span className="text-amber-300 break-all" data-testid="cred-username">{credModal.username}</span></div>
                <div><span className="text-slate-500">Password:</span> <span className="text-amber-300 break-all" data-testid="cred-password">{credModal.password}</span></div>
              </div>
              <p className="text-xs text-slate-500">Al primo accesso il titolare sarà obbligato a impostare una nuova password personale.</p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCredModal(null)} className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
            <Button type="button" onClick={sendCredentials} disabled={credSending} data-testid="confirm-send-credentials" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {credSending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma e Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={impOpen} onOpenChange={(o) => { if (!o) resetImp(); setImpOpen(o); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              <FileJson className="h-5 w-5 text-amber-400" /> Importa Compagnie (JSON / Excel / CSV)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={downloadCompagnieTemplate} data-testid="admin-download-template"
                className="border-slate-700 text-slate-200 hover:bg-slate-800">
                <Download className="h-4 w-4 mr-1.5" /> Template JSON
              </Button>
              <Button type="button" variant="outline" onClick={downloadCompagnieExcelTemplate} data-testid="admin-download-excel-template"
                className="border-slate-700 text-slate-200 hover:bg-slate-800">
                <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Template Excel
              </Button>
            </div>

            <label className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-600 bg-slate-900/40 px-4 py-6 cursor-pointer hover:border-amber-500/50 transition-colors" data-testid="admin-import-file-label">
              <Upload className="h-6 w-6 text-slate-400" />
              <span className="text-sm text-slate-300">{impFileName || "Seleziona un file .json, .xlsx o .csv"}</span>
              <input type="file" accept=".json,application/json,.csv,text/csv,.xlsx,.xls" onChange={onImpFile} className="hidden" data-testid="admin-import-file-input" />
            </label>

            {impCompanies && !impReport && (
              <>
                <CompagniePreview companies={impCompanies} />
                <p className="text-sm text-slate-200"><strong>{impCompanies.length}</strong> compagnie nel file.</p>

                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-mono">Studi destinazione</p>
                  <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="imp-all-studi">
                    <input type="checkbox" checked={impAll} onChange={(e) => setImpAll(e.target.checked)} />
                    Tutti gli studi ({tenants.length})
                  </label>
                  {!impAll && (
                    <div className="max-h-40 overflow-y-auto space-y-1 pl-1 border-t border-slate-800 pt-2">
                      {tenants.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid={`imp-studio-${t.id}`}>
                          <input type="checkbox" checked={impSelected.includes(t.id)} onChange={() => toggleImpStudio(t.id)} />
                          {t.studio_name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                  <p className="text-xs text-slate-400">Come gestire le compagnie già esistenti in uno studio?</p>
                  <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="admin-strategy-skip">
                    <input type="radio" name="admin-strategy" checked={impStrategy === "skip"} onChange={() => setImpStrategy("skip")} />
                    Salta le esistenti (importa solo le nuove)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="admin-strategy-overwrite">
                    <input type="radio" name="admin-strategy" checked={impStrategy === "overwrite"} onChange={() => setImpStrategy("overwrite")} />
                    Sovrascrivi le esistenti
                  </label>
                </div>
              </>
            )}

            {impReport && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm space-y-2" data-testid="admin-import-report">
                <p className="text-emerald-300 font-semibold">Report totale</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-200">
                  <span>Nuove importate: <strong>{impReport.totale.imported}</strong></span>
                  <span>Sovrascritte: <strong>{impReport.totale.updated}</strong></span>
                  <span>Saltate: <strong>{impReport.totale.skipped}</strong></span>
                  <span>Errori: <strong className={impReport.totale.errors ? "text-red-400" : ""}>{impReport.totale.errors}</strong></span>
                </div>
                <div className="border-t border-slate-800 pt-2 max-h-40 overflow-y-auto space-y-1">
                  {impReport.studi.map((s) => (
                    <div key={s.tenant_id} className="flex items-center justify-between text-xs" data-testid={`report-studio-${s.tenant_id}`}>
                      <span className="text-slate-300 truncate mr-2">{s.studio_name}</span>
                      <span className="text-slate-400 font-mono shrink-0">+{s.imported} / ~{s.updated} / skip {s.skipped}{s.errors ? ` / err ${s.errors}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { resetImp(); setImpOpen(false); }}
              className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="admin-import-close">
              {impReport ? "Chiudi" : "Annulla"}
            </Button>
            {!impReport && (
              <Button type="button" onClick={doAdminImport} disabled={!impCompanies || impBusy} data-testid="admin-import-confirm"
                className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {impBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Importa"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
